import * as THREE from 'three';
import { TextEncoder } from 'text-encoding';

import Arrow from '../object-view/objects/arrow';
import Line from '../object-view/objects/line';
import { HTMLEditableLabel } from '../object-view/objects/html-label';
import { APP_TYPES, defaultUnits} from '../app.config';
import { standardList} from '../app.values';

import { DISTANCE, STRENGTH, TEMPERATURE, LOAD, MOMENT, FRACTIONAL_INCHES_DENOMINATOR } from '@reducers/units';

import convertUnits from 'convert-units';

import React from 'react';
import Panel from '@scene/objects/panel';
import { APP_TEXTS } from '@texts';
import { Combination, loadTypes } from './../views/Loads/GeneralLoading/DesignLoading/misc/combination.class';
import loadingParams from './../views/Loads/GeneralLoading/DesignLoading/misc/loadingParams';

export const INFINITY_CHAR = '∞';

/**
 * Simple object check.
 * @param item
 * @returns {boolean}
 */
export function isObject(x) {
  return typeof x === 'object' && !Array.isArray(x) && x !== null;
}

/**
 * Deep merge two objects.
 * @param target
 * @param ...sources
 */
export function mergeDeep(target, ...sources) {
  if (!sources.length) return target;
  const source = sources.shift();

  if (isObject(target) && isObject(source)) {
    // eslint-disable-next-line no-restricted-syntax
    for (const key in source) {
      if (isObject(source[key])) {
        if (!isObject(target[key])) {
          target[key] = {};
        }

        mergeDeep(target[key], source[key]);
      } else {
        target[key] = source[key];
      }
    }
  }

  return mergeDeep(target, ...sources);
}

export function toRadians(angle) {
  return angle * (Math.PI / 180);
}

export function triangleSides(size, position) {
  // Top
  const A = {
    x: 0,
    y: position.y - (size.height / 2)
  };
  // Left
  const B = {
    x: position.x + (size.width / 2),
    y: position.y + (size.height / 2)
  };
  // Right
  const C = {
    x: position.x - (size.width / 2),
    y: position.y + (size.height / 2)
  };

  return { A, B, C };
}

export function resizeTriangle({ A, B, C }, changeTo) {
  /* eslint-disable */
  A = {
    x: A.x / changeTo,
    y: A.y / changeTo
  };
  B = {
    x: B.x / changeTo,
    y: B.y / changeTo
  };
  C = {
    x: C.x / changeTo,
    y: C.y / changeTo
  };
  /* eslint-enable */

  return { A, B, C };
}

export function getTriangle(width, height, position = new THREE.Vector2(0, 0)) {
    const A = {
        x: position.x,
        y: position.y - height / 2
    };
    const B = {
        x: position.x + width / 2,
        y: position.y + height / 2
    };
    const C = {
        x: position.x - width / 2,
        y: position.y + height / 2
    };

  return { A, B, C };
}

export function getDotsOnSide(A, B, count) {
  const dots = [];

  dots.push(A);
  const number = count - 1;

  for (let i = 1; i < number; i++) {
    const lambda = i / (number - i);

    const dot = {
      x: (A.x + (lambda * B.x)) / (1 + lambda),
      y: (A.y + (lambda * B.y)) / (1 + lambda)
    };

    dots.push(dot);
  }

  dots.push(B);

  return dots;
}

export function removeDuplicatesDots(arr) {
  const uniqueArray = [];
  for (let i = 0; i < arr.length; i++) {
    if (!uniqueArray.find(item => item.x === arr[i].x && item.y === arr[i].y)) {
      uniqueArray.push(arr[i]);
    }
  }
  return uniqueArray;
}

export function triangleMiddle({ A, B, C }) {
  return {
    x: (A.x + B.x + C.x) / 3,
    y: (A.y + B.y + C.y) / 3
  };
}

export function triangleGeometry(coords, depth) {
  const shape = new THREE.Shape();

    shape.moveTo(coords[0].y, coords[0].x);
    shape.lineTo(coords[1].y, coords[1].x);
    shape.lineTo(coords[2].y, coords[2].x);
    shape.lineTo(coords[0].y, coords[0].x);

  const extrudeSettings = {
    depth,
    bevelEnabled: false
  };

  return new THREE.ExtrudeGeometry(shape, extrudeSettings);
}

// export function assignUVs(geometry) {
//   geometry.computeBoundingBox();

//   var max = geometry.boundingBox.max,
//     min = geometry.boundingBox.min;
//   var offset = new THREE.Vector2(0 - min.x, 0 - min.y);
//   var range = new THREE.Vector2(max.x - min.x, max.y - min.y);
//   var faces = geometry.faces;

//   geometry.faceVertexUvs[0] = [];

//   for (var i = 0; i < faces.length; i++) {

//     // var components = ['x', 'y', 'z'].sort(function (a, b) {
//     //   return Math.abs(faces[i].normal[a]) > Math.abs(faces[i].normal[b]);
//     // });

//     var v1 = geometry.vertices[faces[i].a],
//       v2 = geometry.vertices[faces[i].b],
//       v3 = geometry.vertices[faces[i].c];

//     geometry.faceVertexUvs[0].push([
//       new THREE.Vector2((v1.x + offset.x) / range.x, (v1.y + offset.y) / range.y),
//       new THREE.Vector2((v2.x + offset.x) / range.x, (v2.y + offset.y) / range.y),
//       new THREE.Vector2((v3.x + offset.x) / range.x, (v3.y + offset.y) / range.y)
//     ]);
//   }
//   geometry.uvsNeedUpdate = true;
// }


export function addDistanceLabel(
    { top, bottom, size, arrowDirection, labelText, labelValue, align, verticalAlign, onChange, arrowSize, disableDepthTest }
  ) {
  const object = new THREE.Object3D();
  const text = getText({ top, size, arrowDirection, labelText, labelValue, align, verticalAlign, onChange });
  const arrow = getArrow({ top, size, arrowDirection, arrowSize, disableDepthTest });
  object.add(arrow.mesh);
  object.add(getLine(top, disableDepthTest));
  object.add(getLine(bottom, disableDepthTest));

  return {
    object,
    text,
    arrow
  };
}

function getText({ top, size, arrowDirection, labelText, labelValue, align, verticalAlign, onChange }) {
  const textLabel = new HTMLEditableLabel({
    text: labelText,
    value: labelValue,
    align,
    verticalAlign,
    minValue: 0
  });
  textLabel.onInputChange(onChange);

  if (arrowDirection === Arrow.types.direction.Z) {
    textLabel.position.set(
      -top.to.y,
      top.to.x,
      top.to.z + (size / 2)
    );
  }

  if (arrowDirection === Arrow.types.direction.Y) {
    textLabel.position.set(
      -top.to.y - (size / 2),
      top.to.x,
      top.to.z
    );
  }

  if (arrowDirection === Arrow.types.direction.X) {
    textLabel.position.set(
      -top.to.x - (size / 2),
      top.to.z,
      -top.to.y
    );
  }

  return textLabel;
}

function getArrow({ top, size, arrowDirection, arrowSize, disableDepthTest }) {
  const arrow = new Arrow({
    position: {
      x: top.to.x || 0,
      y: top.to.y - (arrowDirection === Arrow.types.direction.Z ? 0.5 : 0),
      z: (top.to.z || 0) - (arrowDirection === Arrow.types.direction.Y ? 0.5 : 0)
    },
    size,
    direction: arrowDirection,
    disableDepthTest: disableDepthTest === undefined ? true : disableDepthTest,
    arrowSize 
  });
  return arrow;
}

function getLine(coords, disableDepthTest) {
  return new Line({
    from: {
      x: coords.from.x || 0,
      y: coords.from.y,
      z: coords.from.z || 0
    },
    to: {
      x: coords.to.x || 0,
      y: coords.to.y,
      z: coords.to.z || 0
    },
    disableDepthTest: disableDepthTest === undefined ? true : disableDepthTest
  }).mesh;
}

export function getEndpoints(coordinates, isBend) {

  var coords;
  // VladP: By overriding the coords variable, you cannot get the side endpoints!
  if (isBend) {
    coords = coordinates[0]; // it's not helpful to disable the errors/warnings: eslint disable line
  } else {
    coords = coordinates;
  }

  const positions = {
    left: coords[0],
    right: coords[0],
    top: coords[0],
    bottom: coords[0]
  };

  for (let i = 1; i < coords.length; i++) {
    if (!Number.isFinite(positions.top.z) || positions.top.z > coords[i].z) {
      positions.top = coords[i];
    }
    if (!Number.isFinite(positions.bottom.z) || positions.bottom.z < coords[i].z) {
      positions.bottom = coords[i];
    }
    if (!Number.isFinite(positions.right.y) || positions.right.y > coords[i].y) {
      positions.right = coords[i];
    }
    if (!Number.isFinite(positions.left.y) || positions.left.y < coords[i].y) {
      positions.left = coords[i];
    }
  }

  if (isBend) {
    return {
      ...positions,
      side: getEndpoints(coordinates[1])
    };
  }

  return positions;
}

export function moveObjectPivotToEdge(originObject, axis, width) {
  // VladP: Unable to clone object with no profile mesh
  let children = [];
  // Remove all children elements
  while (originObject.children.length) {
    children.push(originObject.children[0]);
    originObject.remove(originObject.children[0]);
  }
  // the cloning should be working now
  var object;
  try {
    object = originObject.clone();
  } catch (e){
    children.map(child => originObject.add(child));
    return originObject;
  }
  children.map(child => object.add(child));

  const wrapper = new THREE.Object3D();
  object.position[axis] += width / 2;
  wrapper.add(object);

  originObject.add(wrapper);

  originObject.position[axis] -= width / 2;

  return originObject;
}

const defaultPadding = 70;
const edgeSharpLevel = 50;
const minSharpLevel = 15;

export function getSides({ panel, ground }, screwPositions) {
  var padding = {};

  if ((panel.type === Panel.types.CUSTOM) && panel.vertices.positions.length > 1){
    const endpoints = getEndpoints(panel.vertices.positions);
    padding = {
      left: -endpoints.right.y + defaultPadding,
      right: endpoints.left.y + defaultPadding,
      top: -endpoints.top.z + defaultPadding,
      bottom: endpoints.bottom.z + defaultPadding
    };
  } else {
    var centerOffset = panel.center || new THREE.Vector3();
    const panelHalfWidth = panel.width / 2;
    const panelHalfHeight = panel.height / 2;
    padding = {
      left: panelHalfWidth + defaultPadding + centerOffset.x,
      right: panelHalfWidth + defaultPadding - centerOffset.x,
      top: panelHalfHeight + defaultPadding - centerOffset.y,
      bottom: panelHalfHeight + defaultPadding + centerOffset.y
    };
  }


  if (ground.padding) {
    if (ground.padding.C1x !== null) {
      padding.right = screwPositions.left.y + parseInt(ground.padding.C1x);
    }
  
    if (ground.padding.C2x !== null) {
      padding.left = -screwPositions.right.y + parseInt(ground.padding.C2x);
    }
  
    if (ground.padding.C1y !== null) {
      padding.top = screwPositions.bottom.z + parseInt(ground.padding.C1y);
    }
  
    if (ground.padding.C2y !== null) {
      padding.bottom = -screwPositions.top.z + parseInt(ground.padding.C2y);
    }
  }

  return padding;
}

export function getSharpLevel() {
  return Math.random() * (edgeSharpLevel - minSharpLevel) + minSharpLevel;
}

export function prepareNum (num, returnStars, decimals = 2, asString = false) {
  if(num === null || num === undefined || num === 0)
    return returnStars ? '***' : num;

  if (num.toString().split(".")[1]){
    if (Number.isFinite(num)) {
      return asString ? (+num).toFixed(decimals) : +num.toFixed(decimals);
    }
  }
  else {
    if ((num >= 99999) && returnStars){
      return '***';
    }
    return num;
  }

  return returnStars ? '***' : num;
}

export function isValidAppType(appType, designStandard){
  switch (appType){
    case APP_TYPES.GENERAL_CALCULATION:{
      return (
        Number(designStandard) === standardList['EU'] ||
        Number(designStandard) === standardList['ICC.Post'] ||
        Number(designStandard) === standardList['ICC.Cast'] ||
        Number(designStandard) === standardList['AS5216'] ||
        Number(designStandard) === standardList['ANZ'] ||
        Number(designStandard) === standardList['CSA'] ||
        Number(designStandard) === standardList['Masonry'] ||
        Number(designStandard) === standardList['AnnexC']
      );
    }
    case APP_TYPES.SAFETY_BARRIER:
    case APP_TYPES.BRACKET_ATTACHMENT:{
      return (
        Number(designStandard) === standardList['EU'] ||
        Number(designStandard) === standardList['ICC.Post'] ||
        Number(designStandard) === standardList['AS5216'] ||
        Number(designStandard) === standardList['ANZ'] ||
        Number(designStandard) === standardList['CSA'] ||
        Number(designStandard) === standardList['AnnexC']
      );
    }
    case APP_TYPES.REBAR_REINFORCEMENT:{
      return (
        Number(designStandard) === standardList['EU'] ||
        Number(designStandard) === standardList['ANZ'] ||
        Number(designStandard) === standardList['AS5216'] ||
        Number(designStandard) === standardList['AS3600'] 
      );
    }
    case APP_TYPES.WOOD_CONNECTION:{
      return Number(designStandard) === standardList['EU'];
    }
    default: {
      return false;
    }
  }
}

export function isICCorCSA (designStandard) {
  return (
    Number(designStandard) === standardList['ICC.Post'] ||
    Number(designStandard) === standardList['ICC.Cast'] ||
    Number(designStandard) === standardList['CSA']
  );
}

export function unitTransform(value, formatNumber = true) {
  if (!value && !Number.isFinite(value)) throw new TypeError('Value was missed!');

  return {
    from: unitFrom => {
      if (!unitFrom) {
        console.error("Unit from was missed!");
        
        if (unitFrom === DISTANCE.FRACTIONAL_INCHES)
          console.error("Conversion from fractional_inches not yet implemented!");

        return {
          to: () => value
        };
      }

      return {
        to: unitTo => {
          if (!unitTo) {
            console.error("Unit to was missed!");

            return value;
          }

          switch (unitTo) {
            case STRENGTH.NEWTON_SQUARE_MILLIMETERS: {
              if (unitFrom === STRENGTH.POUND_PER_SQUARE_INCH) {
                return formatNumber ? prepareNum(value / 145.038) : value / 145.038;
              }

              return formatNumber ? prepareNum(value) : value;
            }
            case STRENGTH.POUND_PER_SQUARE_INCH: {
              if (unitFrom === STRENGTH.NEWTON_SQUARE_MILLIMETERS) {
                return formatNumber ? prepareNum(value * 145.038) : value * 145.038;
              }

              return formatNumber ? prepareNum(value) : value;
            }
            case LOAD.NEWTON: {
              switch (unitFrom) {
                case LOAD.KILONEWTON: {
                  return formatNumber ? prepareNum(value * 1000) : value * 1000;
                }
                case LOAD.DECANEWTON: {
                  return formatNumber ? prepareNum(value * 10) : value * 10;
                }
                case LOAD.POUND_FORCE: {
                  return formatNumber ? prepareNum(value * 0.224809) : value * 0.224809;
                }
                case LOAD.NEWTON:
                default:
                  return formatNumber ? prepareNum(value) : value;
              }
            }
            case LOAD.KILONEWTON: {
              switch (unitFrom) {
                case LOAD.NEWTON: {
                  return formatNumber ? prepareNum(value / 1000) : value / 1000;
                }
                case LOAD.DECANEWTON: {
                  return formatNumber ? prepareNum(value / 100) : value / 100;
                }
                case LOAD.POUND_FORCE: {
                  return formatNumber ? prepareNum(value * 0.004448221615255) : value * 0.004448221615255;
                }
                case LOAD.KILONEWTON:
                default:
                  return formatNumber ? prepareNum(value) : value;
              }
            }
            case LOAD.DECANEWTON: {
              switch (unitFrom) {
                case LOAD.NEWTON: {
                  return formatNumber ? prepareNum(value / 10) : value / 10;
                }
                case LOAD.KILONEWTON: {
                  return formatNumber ? prepareNum(value * 100) : value * 100;
                }
                case LOAD.POUND_FORCE: {
                  return formatNumber ? prepareNum(value * 0.4448221615255) : value * 0.4448221615255;
                }
                case LOAD.DECANEWTON:
                default:
                  return formatNumber ? prepareNum(value) : value;
              }
            }
            case LOAD.POUND_FORCE: {
              switch (unitFrom) {
                case LOAD.NEWTON: {
                  return formatNumber ? prepareNum(value * 0.224809) : value * 0.224809;
                }
                case LOAD.KILONEWTON: {
                  return formatNumber ? prepareNum(value * 224.8089431) : value * 224.8089431;
                }
                case LOAD.DECANEWTON: {
                  return formatNumber ? prepareNum(value * 2.248089431) : value * 2.248089431;
                }
                case LOAD.POUND_FORCE:
                default:
                  return formatNumber ? prepareNum(value) : value;
              }
            }
            case MOMENT.NEWTON_METERS: {
              switch (unitFrom) {
                case MOMENT.DECANEWTON_METER: {
                  return formatNumber ? prepareNum(value * 10) : value * 10;
                }
                case MOMENT.KILONEWTON_METER: {
                  return formatNumber ? prepareNum(value * 1000) : value * 1000;
                }
                case MOMENT.FOOT_POUND_FORCE: {
                  return formatNumber ? prepareNum(value * 0.74) : value * 0.74;
                }
                case MOMENT.NEWTON_METERS:
                default:
                  return formatNumber ? prepareNum(value) : value;
              }
            }
            case MOMENT.DECANEWTON_METER: {
              switch (unitFrom) {
                case MOMENT.NEWTON_METERS: {
                  return formatNumber ? prepareNum(value / 10) : value / 10;
                }
                case MOMENT.KILONEWTON_METER: {
                  return formatNumber ? prepareNum(value * 100) : value * 100;
                }
                case MOMENT.FOOT_POUND_FORCE: {
                  return formatNumber ? prepareNum(value * 0.4448221615255) : value * 0.4448221615255;
                }
                case MOMENT.DECANEWTON_METER:
                default:
                  return formatNumber ? prepareNum(value) : value;
              }
            }
            case MOMENT.KILONEWTON_METER: {
              switch (unitFrom) {
                case MOMENT.DECANEWTON_METER: {
                  return formatNumber ? prepareNum(value / 100) : value / 100;
                }
                case MOMENT.NEWTON_METERS: {
                  return formatNumber ? prepareNum(value / 1000) : value / 1000;
                }
                case MOMENT.FOOT_POUND_FORCE: {
                  return formatNumber ? prepareNum(value / 737.56214927727) : value / 737.56214927727;
                }
                case MOMENT.KILONEWTON_METER:
                default:
                  return formatNumber ? prepareNum(value) : value;
              }
            }
            case MOMENT.FOOT_POUND_FORCE: {
              switch (unitFrom) {
                case MOMENT.DECANEWTON_METER: {
                  return formatNumber ? prepareNum(value * 7.37562) : value * 7.37562;
                }
                case MOMENT.KILONEWTON_METER: {
                  return formatNumber ? prepareNum(value * 737.56214927727) : value * 737.56214927727;
                }
                case MOMENT.NEWTON_METERS: {
                  return formatNumber ? prepareNum(value * 0.737562) : value * 0.737562;
                }
                case MOMENT.FOOT_POUND_FORCE:
                default:
                  return formatNumber ? prepareNum(value) : value;
              }
            }
            case DISTANCE.FRACTIONAL_INCHES: {
              let inches = 0;
              if (unitFrom !== DISTANCE.INCHES) {
                inches = convertUnits(value).from(unitFrom).to(DISTANCE.INCHES);
              } else {
                inches = value;
              }
              let result = inches.toString().split(".")[0];
              let remain = inches.toString().split(".")[1];
              if (remain) {
                let num = Math.round(Number("0." + remain) * FRACTIONAL_INCHES_DENOMINATOR);
                if (num){
                  var reducedFraction = reduce(num, FRACTIONAL_INCHES_DENOMINATOR);
                  if (result === '0')
                    result = reducedFraction[0] + "/" + reducedFraction[1];
                  else 
                    result += "-" + reducedFraction[0] + "/" + reducedFraction[1];
                }
              }
              return result;
            }
            default: {
              return formatNumber ? 
                prepareNum(convertUnits(value).from(unitFrom).to(unitTo)) : convertUnits(value).from(unitFrom).to(unitTo);
            }
          }
        }
      };
    }
  };
}

export function unitLabel(unit) {
  switch (unit) {
    case STRENGTH.NEWTON_SQUARE_MILLIMETERS: {
      return (<span>N/mm<sup>2</sup></span>);
    }
    case TEMPERATURE.CELSIUS: {
      return '℃';
    }
    case TEMPERATURE.FAHRENHEIT: {
      return '℉';
    }
    default: {
      return unit;
    }
  }
}

export function generatePlanarUVMap(geometry) {
  // calculate the UV coordinates for a planar surface (x, y, z) where z = 0 
  geometry.computeBoundingBox();

  const max = geometry.boundingBox.max;
  const min = geometry.boundingBox.min;

  const offset = new THREE.Vector2(0 - min.x, 0 - min.y);
  const range = new THREE.Vector2(max.x - min.x, max.y - min.y);

  var uvAttributes = geometry.attributes.uv;
  var vertices = geometry.attributes.position;
  for (let i = 0; i < vertices.count; i++) {
    uvAttributes.setXY(i, (vertices.getX(i) + offset.x) / range.x, (vertices.getY(i) + offset.y) / range.y);
  }

  uvAttributes.needsUpdate = true;
  return geometry;
}

function reduce(a, b){
  for (let i = 2; i <= b / 2; i++){
    if (a % i === 0 && b % i === 0)
      return reduce(a/i, b/i);
  }

  return [a, b];
}

export function download(filename, text) {
  // polyfill for IE / oldversions of Edge
  if (!window['TextEncoder']) {
    window['TextEncoder'] = TextEncoder;
  }

  const encodedText = new TextEncoder('utf-8').encode(text);
  var blob = new Blob([encodedText], { type: 'application/json'});
  
  // IE doesn't allow using a blob object directly as link href
  // instead it is necessary to use msSaveOrOpenBlob
  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(blob, filename);
  } 
  else {
    // For other browsers: 
    // Create a link pointing to the ObjectURL containing the blob.
    const url = window.URL.createObjectURL(blob);
    const element = document.createElement('a');
    element.setAttribute('href', url);
    element.setAttribute('download', filename);

    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);

    setTimeout(function(){
      window.URL.revokeObjectURL(url);
    }, 300000); // revoke after 5 min
  }
}

export function readFile(acceptAtt) {
  return new Promise((resolve, reject) => {
    var element = document.createElement('input');
    element.setAttribute('type', 'file');
    if (acceptAtt)
      element.setAttribute('accept', acceptAtt);

    element.addEventListener('change', e => {
      document.body.removeChild(element);
      element = null;
      const file = e.target.files[0];
      if (!file) {
        reject();
      }

      const reader = new FileReader();
      reader.onload = function(e) {
        const contents = e.target.result;

        resolve({contents, fileName: file.name});
      };
      reader.readAsText(file);
    });

    element.style.display = 'none';
    document.body.appendChild(element);

    element.click();
  });
}

export function sortPolygonPoints(points) {
  if (null == points || !(points.length && points.length > 0))
    return;

  var x = 0, y = 0;
  for (let i = 0; i < points.length; i++) {
    x += points[i].x;
    y += points[i].y;
  }

  var center = new THREE.Vector2(x / points.length, y / points.length);

  points.sort((a, b) => {
    var a1 = Math.atan2(a.x - center.x, a.y - center.y);
    var a2 = Math.atan2(b.x - center.x, b.y - center.y);

    return (a1 - a2);
  });
}

export function getCenterOfPoints(points) {
  if (null == points || !((points.length && points.length > 0) || (points.count && points.count > 0)))
    return;

  var sumX = 0, sumY = 0, sumZ = 0;
  for (let i = 0; i < points.length; i++) {
    sumX += points[i].x;
    sumY += points[i].y;
    sumZ += points[i].z;
  }

  return new THREE.Vector3(sumX / points.length, sumY / points.length, sumZ / points.length);
}

export function getInteractionMaximumValue(selItem, designStandard){
  // if ((Number(designStandard) === standardList["Masonry"] && selItem.BetaN_Masonry > 0 && selItem.BetaV_Masonry > 0) ||
  if (Number(designStandard) === standardList["ANZ"] && selItem.BetaN > 0 && selItem.BetaV > 0)
    return 120;
  else
    return 100;
}

export function validateCombinationsInput(input) {
  let ret = {
    isValid: false,
    error: null,
    separator: ',',
    hasHeaders: false,
    headers: [],
    cleanedInput: null
  };

  let rows = input.split(/\r\n|\r|\n/g);
  if (rows.length < 1) {
    return ret;
  }
  // get separator used
  let firstRow = rows[0];
  if (firstRow.includes('\t')) {
    ret.separator = '\t';
  } else if (firstRow.includes(';')) {
    ret.separator = ';';
  } else if (!firstRow.includes(',')) {
    return ret;
  }

  // clean csv/tsv input
  if (input.endsWith("\r") || input.endsWith("\n")) {
    ret.cleanedInput = input;
    while (ret.cleanedInput.endsWith("\r\n"))
      ret.cleanedInput = ret.cleanedInput.substring(0, ret.cleanedInput.length - 2);
    while (ret.cleanedInput.endsWith("\r") || ret.cleanedInput.endsWith("\n"))
      ret.cleanedInput = ret.cleanedInput.substring(0, ret.cleanedInput.length - 1);
  }

  // this will prevent importing files with no header name for combination/load type
  // if (input.includes(ret.separator + ret.separator)) {
  //   if (!ret.cleanedInput)
  //     ret.cleanedInput = input;
  //   let 
  //   do 
  //   {
  //     ret.cleanedInput = ret.cleanedInput.replaceAll(ret.separator + ret.separator, ret.separator);
  //   } while (ret.cleanedInput.includes(ret.separator + ret.separator));
  // }
  if (ret.cleanedInput) {
    rows = ret.cleanedInput.split(/\r\n|\r|\n/g);
    firstRow = rows[0];
  }

  // check columns count
  let firstRowValues = firstRow.split(ret.separator);
  if (firstRowValues.length < 6 || firstRowValues.length > 8){
    return ret;
  } 

  // check headers
  let availableLoadHeaders = getAvailableLoadHeaders();
  let nanCount = 0;
  let i = 0;
  for (i = 0; i < firstRowValues.length; i++) {
    if (firstRowValues[i].search(/[A-DF-df-z]/) !== -1) 
      nanCount++;
    if (nanCount > 2)
      break;
  }
  if (nanCount > 2) {
    if (rows.length < 2) {
      return ret;
    }
    ret.hasHeaders = true;
    let secondRowValues = rows[1].split(ret.separator);
    let tempIdx;
    for (i = 0; i < firstRowValues.length; i++) {
      tempIdx = availableLoadHeaders.indexOf(firstRowValues[i]);
      if (tempIdx === -1) { // can be combination name or loadtype
        if (secondRowValues[i].search(/[A-DF-df-z]/) !== -1) { // contains a non-digit character
          if (secondRowValues[i].toLowerCase() === "standard" || 
            secondRowValues[i].toLowerCase() === APP_TEXTS.LOADS_TYPE_STANDARD.Content || 
            secondRowValues[i].toLowerCase() === "seismic" || secondRowValues[i].toLowerCase() === APP_TEXTS.LOADS_TYPE_SEISMIC.Content || 
            secondRowValues[i].toLowerCase() === "fire" || secondRowValues[i].toLowerCase() === APP_TEXTS.LOADS_TYPE_FIRE.Content) {
              ret.headers[i] = "loadType";
          } else {
            if (ret.headers.indexOf("name") === -1)
              ret.headers[i] = "name";
            else 
              return ret; // multiple not recognized columns
          }
        } else if (Number.parseInt(secondRowValues[i]) < 3) {
          ret.headers[i] = "loadType";
        } else {
          return ret; 
        }
      } else {
        tempIdx = tempIdx % 6;
        ret.headers[i] = availableLoadHeaders[tempIdx];
      }
    }
  } else { // nanCount < 2
    for (i = 0; i < firstRowValues.length; i++) {
      // if (Number.isNaN(Number.parseFloat(firstRowValues[i]))) {
      if (firstRowValues[i].search(/[A-DF-df-z]/) !== -1) { // contains a non-digit character
        if (firstRowValues[i].toLowerCase() === "standard" || 
          firstRowValues[i].toLowerCase() === APP_TEXTS.LOADS_TYPE_STANDARD.Content.toLowerCase() || 
          firstRowValues[i].toLowerCase() === "seismic" || 
          firstRowValues[i].toLowerCase() === APP_TEXTS.LOADS_TYPE_SEISMIC.Content.toLowerCase() || 
          firstRowValues[i].toLowerCase() === "fire" || 
          firstRowValues[i].toLowerCase() === APP_TEXTS.LOADS_TYPE_FIRE.Content.toLowerCase()) {
            ret.headers[i] = "loadType";
        } else if (ret.headers.indexOf("name") === -1) {
            ret.headers[i] = "name";
        }
      } else if (firstRowValues.length - i > 6 && Number.parseInt(firstRowValues[i]) < 3) { // extra columns besides the loads
        ret.headers[i] = "loadType";
      } else { // set the loads name based on the sorted load header
        if (i === 0)
          ret.headers[i] = availableLoadHeaders[0];
        else {
          let idx = availableLoadHeaders.indexOf(ret.headers[i -1]);
          if (idx === -1)
            ret.headers[i] = availableLoadHeaders[0];
          else 
            ret.headers[i] = availableLoadHeaders[idx + 1];
        }
      }
    }
  }
  
  //check unique headers
  let set = new Set(ret.headers);
  if (ret.headers.length !== set.size)
    return ret;

  // check file integrity
  let tempRowValues;
  for (i = ret.hasHeaders ? 1 : 0; i < rows.length; i++) {
    tempRowValues = rows[i].split(ret.separator);
    if (tempRowValues.length !== firstRowValues.length)
      return ret;
    for (let j = 0; j < tempRowValues.length; j++) {
      if (availableLoadHeaders.indexOf(ret.headers[j]) !== -1 && Number.isNaN(Number.parseFloat(tempRowValues[j])))
        return ret;
    }
  }

  ret.isValid = true;
  return ret;
}

export function getAvailableLoadHeaders() {
  return [
    "Nz", "Vx", "Vy", "Mz", "Mx", "My",
    "NEd,z", "VEd,x", "VEd,y", "MEd,z", "MEd,x", "MEd,y",
    "Nz*", "Vx*", "Vy*", "Mz*", "Mx*", "My*",
    "Nuz", "Vux", "Vuy", "Muz", "Mux", "Muy",
    "N"
  ];
}

export function createCombinationsFromInput(validationResult, input, loadUnit, momentUnit) {
  let data = [];
  let rows = input ? input.split(/\r\n|\r|\n/g) : validationResult.cleanedInput.split(/\r\n|\r|\n/g);
  let loadHeaders = getAvailableLoadHeaders();
  let k = -1;
  let tempRowValues, tempLoadType, tempLoadTypeInt, tempValue;
  for (let i = validationResult.hasHeaders ? 1 : 0; i < rows.length; i++) {
    tempRowValues = rows[i].split(validationResult.separator);
    data[++k] = new Combination(k);

    for (let j = 0; j < validationResult.headers.length; j++) {
      if (validationResult.headers[j] === "loadType") {
        tempLoadType = loadTypes.STANDARD; 
        tempLoadTypeInt = Number.parseInt(tempRowValues[j]);
        if (Number.isNaN(tempLoadTypeInt)) {
          if (tempRowValues[j].toLowerCase() === "seismic" || 
            tempRowValues[j].toLowerCase() === APP_TEXTS.LOADS_TYPE_SEISMIC.Content.toLowerCase())
              tempLoadType = loadTypes.SEISMIC;
          else if (tempRowValues[j].toLowerCase() === "fire" || 
            tempRowValues[j].toLowerCase() === APP_TEXTS.LOADS_TYPE_FIRE.Content.toLowerCase())
              tempLoadType = loadTypes.FIRE;
        } else {
          switch (tempLoadTypeInt) {
            case 1: { tempLoadType = loadTypes.SEISMIC; break; }
            case 2: { tempLoadType = loadTypes.FIRE; break; }
            case 0:
            default: { tempLoadType = loadTypes.STANDARD; break; }
          }
        }
        data[k][validationResult.headers[j]] = tempLoadType;
      } else {
        tempValue = tempRowValues[j];
        if (loadHeaders.indexOf(validationResult.headers[j]) >= 0 && loadHeaders.indexOf(validationResult.headers[j]) <= 2)
          tempValue = unitTransform(parseFloatInvariantCulture(tempValue) || 0, false).from(loadUnit).to(defaultUnits.LOAD);
        else if (loadHeaders.indexOf(validationResult.headers[j]) >= 3 && loadHeaders.indexOf(validationResult.headers[j]) <= 5)
          tempValue = unitTransform(parseFloatInvariantCulture(tempValue) || 0, false).from(momentUnit).to(defaultUnits.MOMENT);
        data[k][validationResult.headers[j]] = tempValue;
      }
    }
  }
  if (validationResult.hasHeaders) {
    let headers = validationResult.headers.filter(h => loadingParams.findIndex(l => l.key === h) !== -1);
    for (let i = 0; i < data.length; i++) {
      data[i] = data[i].switchValuesOrder(headers);
    }
  }

  return data;
}

export function parseFloatInvariantCulture(stringValue) {
  let commaIdx = stringValue.indexOf(',');
  let dotIdx = stringValue.indexOf('.');
  if (commaIdx !== -1) {
    if (dotIdx !== -1) {
      let list = stringValue.split(/[,|.]/);
      let decimals = list.pop();
      return +(list.join('') + '.' + decimals); // "1,200,200.56" || "1.200.200,56" => 1200200.56
    } else if (commaIdx === stringValue.lastIndexOf(',')) {
      return +(stringValue.replace(',', '.')); // "1000,5" => 1000.5
    } else {
      return +(stringValue.replaceAll(',', '')); // "1,200,200" => 1200200
    }
  } else if (dotIdx !== -1) {
    if (dotIdx === stringValue.lastIndexOf('.')) {
      return +stringValue; // "1000.5" => 1000.5
    } else {
      return +(stringValue.replaceAll('.', '')); // "1.200.200" => 1200200
    }
  } else {
    return +stringValue; // "1000" => 1000
  }
}



// WEBPACK FOOTER //
// ./src/services/helper.service.js